
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { User, ChevronDown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useBrandManager } from "@/hooks/useBrandManager";

interface BrandUser {
  id: string;
  email: string;
  full_name: string;
  role: string;
}

interface UserSelectorProps {
  currentUserId: string;
  onUserSelect: (userId: string) => void;
  label?: string;
}

export const UserSelector = ({ currentUserId, onUserSelect, label = "Assign to User" }: UserSelectorProps) => {
  const [brandUsers, setBrandUsers] = useState<BrandUser[]>([]);
  const [currentUser, setCurrentUser] = useState<BrandUser | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { currentBrandId } = useBrandManager();

  const fetchBrandUsers = async () => {
    if (!currentBrandId) return;

    setIsLoading(true);
    try {
      // First get all user IDs who have access to this brand
      const { data: brandAccess, error: accessError } = await supabase
        .from('user_brand_access')
        .select('user_id, role')
        .eq('brand_id', currentBrandId);

      if (accessError) throw accessError;

      if (!brandAccess || brandAccess.length === 0) {
        setBrandUsers([]);
        setCurrentUser(null);
        return;
      }

      // Get unique user IDs and filter out empty ones
      const userIds = [...new Set(brandAccess.map(access => access.user_id))]
        .filter(id => id && id.trim() !== '');

      if (userIds.length === 0) {
        setBrandUsers([]);
        setCurrentUser(null);
        return;
      }

      // Now get the profile information for these users
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, email, full_name')
        .in('id', userIds);

      if (profilesError) throw profilesError;

      // Combine the data
      const users = brandAccess
        .filter(access => access.user_id && access.user_id.trim() !== '')
        .map(access => {
          const profile = profiles?.find(p => p.id === access.user_id);
          return {
            id: access.user_id,
            email: profile?.email || '',
            full_name: profile?.full_name || profile?.email || 'Unknown User',
            role: access.role
          };
        })
        .filter(user => user.id); // Additional filter to ensure no empty IDs

      setBrandUsers(users);

      // Find current user
      const current = users.find(user => user.id === currentUserId);
      setCurrentUser(current || null);

    } catch (error: any) {
      console.error('Error fetching brand users:', error);
      toast({
        title: "Error",
        description: "Failed to fetch brand users",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBrandUsers();
  }, [currentBrandId, currentUserId]);

  if (isLoading) {
    return (
      <div className="space-y-2">
        <label className="text-sm font-medium">{label}</label>
        <div className="animate-pulse bg-gray-200 h-10 rounded-md"></div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <label className="text-sm font-medium">{label}</label>
      
      {/* Current User Display */}
      {currentUser && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium">Current: {currentUser.full_name}</span>
              <Badge variant="secondary" className="text-xs">
                {currentUser.role}
              </Badge>
            </div>
            <p className="text-xs text-gray-600 mt-1">{currentUser.email}</p>
          </CardContent>
        </Card>
      )}

      {/* User Selection */}
      <Select value={currentUserId} onValueChange={onUserSelect}>
        <SelectTrigger>
          <SelectValue placeholder="Select a user from your brand">
            {currentUser ? (
              <span className="flex items-center gap-2">
                <User className="h-4 w-4" />
                {currentUser.full_name}
              </span>
            ) : (
              "Select a user"
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {brandUsers.length === 0 ? (
            <div className="p-2 text-center text-sm text-gray-500">
              No users found in this brand
            </div>
          ) : (
            brandUsers.map((user) => (
              <SelectItem key={user.id} value={user.id}>
                <div className="flex items-center gap-2 w-full">
                  <User className="h-4 w-4" />
                  <div className="flex-1">
                    <div className="font-medium">{user.full_name}</div>
                    <div className="text-xs text-gray-500">{user.email}</div>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {user.role}
                  </Badge>
                </div>
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>

      {brandUsers.length > 1 && (
        <p className="text-xs text-gray-500">
          {brandUsers.length} users available in this brand
        </p>
      )}
    </div>
  );
};
